import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ScrollShowcase pins for +=600% of viewport height → 6 × innerHeight of scroll
const HERO_SCROLL_MULTIPLIER = 6;

const Navbar = () => {
  const [show, setShow] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => {
      setShow(window.scrollY >= window.innerHeight * HERO_SCROLL_MULTIPLIER * 0.97);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-sky-100 transition-all duration-300 ${
        show ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo: "Rental" text + brand X icon (SVG with transparent bg) */}
          <Link to="/" className="flex items-center gap-0 h-full" data-testid="navbar-logo">
            <span className="text-2xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <span
              className="inline-block overflow-hidden"
              style={{ width: '1.9rem', height: '1.7rem', flexShrink: 0 }}
            >
              {/* X in SVG: scale to zoom into the X area (x:980-1248, y:638-874 in 1500×1500 canvas) */}
              {/* On white navbar: white PNG bg is invisible → X colors show naturally */}
              <img
                src="/rentalx-x.svg"
                alt="X"
                style={{
                  width: '100%',
                  height: '100%',
                  transform: 'scale(6.4)',
                  transformOrigin: '74.2% 50.4%',
                  display: 'block',
                }}
              />
            </span>
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/vibemate"
              className="text-sm font-semibold text-gray-600 hover:text-[#094f6f] transition-colors hidden sm:block"
              data-testid="navbar-vibemate-link"
            >
              VibeMate
            </Link>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="text-sm text-gray-600 hidden sm:block">
                  {user.full_name || user.email}
                </span>
                <Link to={user.role === 'landlord' ? '/landlord-dashboard' : '/student-dashboard'}>
                  <Button variant="outline" size="sm" className="border-sky-200 text-[#094f6f]" data-testid="navbar-dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-gray-500 hover:text-red-500"
                  data-testid="navbar-logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button
                  className="bg-[#094f6f] text-white hover:bg-[#094f6f]/90 flex items-center gap-2"
                  data-testid="navbar-login-btn"
                >
                  <User className="w-4 h-4" />
                  Login
                </Button>
              </Link>
            )}
          </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
